/**
 * Place category metadata (icon + color + label) used everywhere on the
 * frontend: filter chip bar, landing-page quick links, ParkCard badge,
 * ParkDetail per-type sections.
 *
 * Keep `key` in sync with backend CATEGORY_CONFIG in google_places.py.
 */
import {
  PawPrint, Waves, UtensilsCrossed, Stethoscope, Scissors,
  Home, Dumbbell, ShoppingBag, Trees, HeartHandshake,
} from "lucide-react";

export const CATEGORIES = [
  { key: "park",       urlSlug: "dog-parks",                label: "Parks",       short: "Parks",       icon: PawPrint,         color: "#4A7C59", bg: "bg-[#4A7C59]", seoLabel: "Dog Parks",                seoShort: "dog parks" },
  { key: "beach",      urlSlug: "dog-beaches",              label: "Beaches",     short: "Beaches",     icon: Waves,            color: "#1F7A8C", bg: "bg-[#1F7A8C]", seoLabel: "Dog-Friendly Beaches",     seoShort: "dog-friendly beaches" },
  { key: "restaurant", urlSlug: "dog-friendly-restaurants", label: "Restaurants", short: "Dining",      icon: UtensilsCrossed,  color: "#B85C38", bg: "bg-[#B85C38]", seoLabel: "Pet-Friendly Restaurants", seoShort: "pet-friendly restaurants" },
  { key: "vet",        urlSlug: "vets",                     label: "Vets",        short: "Vets",        icon: Stethoscope,      color: "#7B4B94", bg: "bg-[#7B4B94]", seoLabel: "Veterinarians",            seoShort: "vets" },
  { key: "groomer",    urlSlug: "groomers",                 label: "Groomers",    short: "Groomers",    icon: Scissors,         color: "#C2185B", bg: "bg-[#C2185B]", seoLabel: "Dog Groomers",             seoShort: "dog groomers" },
  // Pilot category — no organic listings yet, populated only via
  // sponsored partners for now. Ordered after Groomers so the
  // "care services" group (groom / sit / board / train) reads
  // as one cluster and the long "Pet Sitters & Dog Walkers" chip
  // sits inside the default viewport instead of being scrolled
  // off the tail of the row. Warm sage color chosen to sit
  // naturally alongside the existing cream/green palette.
  { key: "pet-sitter", urlSlug: "pet-sitters",              label: "Pet Sitters & Dog Walkers", short: "Pet Sitters & Dog Walkers", icon: HeartHandshake, color: "#6B8E5A", bg: "bg-[#6B8E5A]", seoLabel: "Pet Sitters & Dog Walkers", seoShort: "pet sitters & dog walkers" },
  { key: "boarding",   urlSlug: "boarding",                 label: "Boarding",    short: "Boarding",    icon: Home,             color: "#5D4037", bg: "bg-[#5D4037]", seoLabel: "Dog Boarding & Daycare",   seoShort: "dog boarding" },
  { key: "trainer",    urlSlug: "trainers",                 label: "Trainers",    short: "Trainers",    icon: Dumbbell,         color: "#388E3C", bg: "bg-[#388E3C]", seoLabel: "Dog Trainers",             seoShort: "dog trainers" },
  { key: "store",      urlSlug: "pet-stores",               label: "Pet stores",  short: "Stores",      icon: ShoppingBag,      color: "#F57C00", bg: "bg-[#F57C00]", seoLabel: "Pet Stores",               seoShort: "pet stores" },
  { key: "trail",      urlSlug: "trails",                   label: "Trails",      short: "Trails",      icon: Trees,            color: "#558B2F", bg: "bg-[#558B2F]", seoLabel: "Dog-Friendly Trails",      seoShort: "dog-friendly trails" },
];

const BY_KEY = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));
const BY_URL_SLUG = Object.fromEntries(CATEGORIES.map((c) => [c.urlSlug, c]));

export const getCategory = (key) => BY_KEY[key] || BY_KEY.park;

/** Look up a category by its URL slug ("dog-parks") rather than its
 *  short key ("park"). Returns null on miss (used by the new
 *  /{category-slug}/{city-slug} root route so unknown slugs fall
 *  through to the city-only handler). */
export const getCategoryByUrlSlug = (slug) => (slug ? BY_URL_SLUG[slug] || null : null);

// Which categories have a dedicated hub route registered in App.js
// (`<Route path="/{urlSlug}" element={<Search categoryUrlSlug=... />}`).
// Keep this list in sync with App.js. `pet-sitter` is intentionally
// excluded — no `/pet-sitters` route exists today, so homepage/footer
// links for sitters still point at `/search?category=pet-sitter`
// (which /search renders in place as a filter — see the 10/2026
// chip-stays-in-place fix in pages/Search.jsx).
const HUB_ROUTE_KEYS = new Set([
  "park", "beach", "restaurant", "vet", "groomer",
  "boarding", "trainer", "store", "trail",
]);

/** Internal link target for a category chip / list item. Returns the
 *  canonical indexable hub URL (`/dog-parks`) when a Route exists for
 *  it, else falls back to the in-place filter URL
 *  (`/search?category=pet-sitter`). Use this instead of hand-rolling
 *  `/search?category=${key}` so adding a future hub (e.g. a new
 *  `/pet-sitters` route) only requires one edit here. */
export const categoryHref = (keyOrCategory) => {
  const key = typeof keyOrCategory === "string" ? keyOrCategory : keyOrCategory?.key;
  const cat = BY_KEY[key];
  if (cat && HUB_ROUTE_KEYS.has(key)) return `/${cat.urlSlug}`;
  return `/search?category=${encodeURIComponent(key)}`;
};

// Which categories show the community "Confirm dog-friendly" button.
// Parks/vets/groomers are obviously dog-friendly so we skip them.
export const VERIFIABLE_CATEGORIES = new Set(["restaurant", "beach", "trail"]);

// Which categories support check-ins, and what kind.
// "dog"    → existing flow with dog selection
// "simple" → "I'm here with my dog" button, no dog selection
// "none"   → no check-in (privacy: vets, groomers, boarding, trainer, store)
export const CHECKIN_STYLE = Object.fromEntries(
  CATEGORIES.map((c) => [
    c.key,
    c.key === "park"
      ? "dog"
      : ["beach", "restaurant", "trail"].includes(c.key)
      ? "simple"
      : "none",
  ])
);
